
import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Switch,
  FormControlLabel, Alert, CircularProgress, Divider, Chip, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PaidIcon from '@mui/icons-material/Paid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const REFERRAL_API = process.env.REACT_APP_REFERRAL_API;

const SERVICE_ORDER = ['medicine', 'diagnostic', 'nursing', 'physiotherapy'];

const DEFAULT_SERVICE_CONFIGS = SERVICE_ORDER.reduce((acc, st) => {
  acc[st] = {
    service_type: st,
    display_name: st,
    redemption_enabled: true,
    max_redemption_percentage: 10,
  };
  return acc;
}, {});

const DEFAULT_EARNING_CONFIG = {
  referrer_bonus_points: 100,
  welcome_bonus_points: 50,
  min_expiry_days: 1,
  max_expiry_days: 30,
  return_window_days: 7,
  is_active: true,
};

const DEFAULT_POINT_VALUE_CONFIG = {
  point_value: 1,
};

// ── shared auth header helper ─────────────────────────────
const authHeaders = (extra = {}) => {
  const token = sessionStorage.getItem('token');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

// Turns a field key like "referrer_bonus_points" into "Referrer bonus points"
const humanizeKey = (key) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const RewardConfigTab = () => {
  // ── Earning (referral) config ─────────────────────────────
  const [earningConfig, setEarningConfig] = useState(null);
  const [earningLoading, setEarningLoading] = useState(true);
  const [earningSaving, setEarningSaving] = useState(false);

  // ── Global point value ──────────────────────────────────────
  const [pointValueConfig, setPointValueConfig] = useState(null);
  const [pointValueLoading, setPointValueLoading] = useState(true);
  const [pointValueSaving, setPointValueSaving] = useState(false);

  // ── Redemption (per-service) config ───────────────────────
  const [serviceConfigs, setServiceConfigs] = useState(null);
  const [serviceLoading, setServiceLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Success dialog: shown after ANY save, with the backend's response ──
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    title: '',
    data: null,
  });

  const openSuccessDialog = (title, data) => {
    setSuccessDialog({ open: true, title, data });
  };

  const closeSuccessDialog = () => {
    setSuccessDialog({ open: false, title: '', data: null });
  };

  useEffect(() => {
    fetchEarningConfig();
    fetchPointValueConfig();
    fetchServiceConfigs();
  }, []);

  // ────────────────────────────────────────────────────────────
  // EARNING CONFIG (referral bonus points)
  // ────────────────────────────────────────────────────────────

  const fetchEarningConfig = async () => {
    setEarningLoading(true);
    try {
      const response = await fetch(`${REFERRAL_API}/admin/referral/config`, {
        method: 'GET',
        headers: authHeaders(),
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got "${contentType}" (status ${response.status}). Response started with: ${text.slice(0, 80)}`);
      }
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to load referral settings (${response.status})`);
      }
      const data = await response.json();
      setEarningConfig(data.config || DEFAULT_EARNING_CONFIG);
    } catch (err) {
      console.error('fetchEarningConfig error:', err);
      setError(err.message || 'Failed to load referral earning settings');
      setEarningConfig(DEFAULT_EARNING_CONFIG);
    } finally {
      setEarningLoading(false);
    }
  };

  const handleEarningChange = (field) => (e) => {
    const value = field === 'is_active' ? e.target.checked : e.target.value;
    setEarningConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleEarningSave = async () => {
    setEarningSaving(true);
    setError('');
    try {
      const payload = {
        referrer_bonus_points: parseFloat(earningConfig.referrer_bonus_points),
        welcome_bonus_points: parseFloat(earningConfig.welcome_bonus_points),
        min_expiry_days: parseInt(earningConfig.min_expiry_days),
        max_expiry_days: parseInt(earningConfig.max_expiry_days),
        return_window_days: parseInt(earningConfig.return_window_days),
        is_active: earningConfig.is_active,
      };
      const response = await fetch(`${REFERRAL_API}/admin/referral/config`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got "${contentType}" (status ${response.status}): ${text.slice(0, 80)}`);
      }
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to update settings (${response.status})`);
      }
      const data = await response.json();
      setEarningConfig(data.config);
      setSuccess('Referral earning settings updated');
      setTimeout(() => setSuccess(''), 3000);
      openSuccessDialog('Referral earning settings saved', data.config);
    } catch (err) {
      setError(err.message || 'Failed to update earning settings');
    } finally {
      setEarningSaving(false);
    }
  };

  // ────────────────────────────────────────────────────────────
  // GLOBAL POINT VALUE (shared across all services)
  // ────────────────────────────────────────────────────────────

  const fetchPointValueConfig = async () => {
    setPointValueLoading(true);
    try {
      const response = await fetch(`${REFERRAL_API}/admin/wallet-point-value`, {
        method: 'GET',
        headers: authHeaders(),
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got "${contentType}" (status ${response.status}). Response started with: ${text.slice(0, 80)}`);
      }
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to load point value settings (${response.status})`);
      }
      const data = await response.json();
      setPointValueConfig(data.config || DEFAULT_POINT_VALUE_CONFIG);
    } catch (err) {
      console.error('fetchPointValueConfig error:', err);
      setError(err.message || 'Failed to load point value settings');
      setPointValueConfig(DEFAULT_POINT_VALUE_CONFIG);
    } finally {
      setPointValueLoading(false);
    }
  };

  const handlePointValueChange = (e) => {
    setPointValueConfig((prev) => ({ ...prev, point_value: e.target.value }));
  };

  const handlePointValueSave = async () => {
    setPointValueSaving(true);
    setError('');
    try {
      const payload = { point_value: parseFloat(pointValueConfig.point_value) };
      const response = await fetch(`${REFERRAL_API}/admin/wallet-point-value`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got "${contentType}" (status ${response.status}): ${text.slice(0, 80)}`);
      }
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to update point value (${response.status})`);
      }
      const data = await response.json();
      setPointValueConfig(data.config);
      setSuccess('Point value updated');
      setTimeout(() => setSuccess(''), 3000);
      openSuccessDialog('Wallet point value saved', data.config);
    } catch (err) {
      setError(err.message || 'Failed to update point value');
    } finally {
      setPointValueSaving(false);
    }
  };

  // ────────────────────────────────────────────────────────────
  // REDEMPTION CONFIG (per-service, percentage of bill)
  // ────────────────────────────────────────────────────────────

  const fetchServiceConfigs = async () => {
    setServiceLoading(true);
    try {
      const response = await fetch(`${REFERRAL_API}/admin/reward-config`, {
        method: 'GET',
        headers: authHeaders(),
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got "${contentType}" (status ${response.status}). Response started with: ${text.slice(0, 80)}`);
      }
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to load reward settings (${response.status})`);
      }
      const data = await response.json();
      setServiceConfigs(data.configs || DEFAULT_SERVICE_CONFIGS);
    } catch (err) {
      console.error('fetchServiceConfigs error:', err);
      setError(err.message || 'Failed to load reward settings');
      setServiceConfigs(DEFAULT_SERVICE_CONFIGS);
    } finally {
      setServiceLoading(false);
    }
  };

  const handleServiceChange = (serviceType, field) => (e) => {
    const value = field === 'redemption_enabled' ? e.target.checked : e.target.value;
    setServiceConfigs((prev) => ({
      ...prev,
      [serviceType]: { ...prev[serviceType], [field]: value },
    }));
  };

  const handleServiceSave = async (serviceType) => {
    setSavingKey(serviceType);
    setError('');
    try {
      const cfg = serviceConfigs[serviceType];
      const payload = {
        redemption_enabled: !!cfg.redemption_enabled,
        max_redemption_percentage: parseFloat(cfg.max_redemption_percentage),
      };
      const response = await fetch(`${REFERRAL_API}/admin/reward-config/${serviceType}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Expected JSON but got "${contentType}" (status ${response.status}): ${text.slice(0, 80)}`);
      }
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to update settings (${response.status})`);
      }
      const data = await response.json();
      const displayName = serviceConfigs[serviceType].display_name || serviceType;
      setServiceConfigs((prev) => ({
        ...prev,
        [serviceType]: { ...data.config, display_name: prev[serviceType].display_name },
      }));
      setSuccess(`${displayName} reward settings updated successfully`);
      setTimeout(() => setSuccess(''), 3000);
      openSuccessDialog(`${displayName} settings saved`, data.config);
    } catch (err) {
      setError(err.message || 'Failed to update settings');
    } finally {
      setSavingKey(null);
    }
  };

  const loading = earningLoading || serviceLoading || pointValueLoading;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: '#006D77' }} />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* ══════════════════ EARNING SECTION ══════════════════ */}
      {!earningConfig ? (
        <Alert
          severity="error"
          sx={{ mb: 3, maxWidth: 900 }}
          action={<Button color="inherit" size="small" onClick={fetchEarningConfig} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          Failed to load referral earning settings.
        </Alert>
      ) : (
        <Paper sx={{ p: 3, maxWidth: 900, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CardGiftcardIcon sx={{ color: '#006D77' }} />
            <Typography variant="h6" fontWeight="bold">Referral earning settings</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Controls how many points a referrer and their referred friend earn, and when
            those points become spendable. Points are credited only after the qualifying
            order is delivered, and only become redeemable once the return window has closed.
          </Typography>

          <FormControlLabel
            sx={{ mb: 2 }}
            control={
              <Switch
                checked={!!earningConfig.is_active}
                onChange={handleEarningChange('is_active')}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#006D77' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#006D77' },
                }}
              />
            }
            label={earningConfig.is_active ? 'Referral program is active' : 'Referral program is paused'}
          />

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="number" label="Referrer bonus (points)"
                value={earningConfig.referrer_bonus_points}
                onChange={handleEarningChange('referrer_bonus_points')}
                disabled={!earningConfig.is_active}
                helperText="Points credited to the referrer when their friend's first order is delivered"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="number" label="Welcome bonus (points)"
                value={earningConfig.welcome_bonus_points}
                onChange={handleEarningChange('welcome_bonus_points')}
                disabled={!earningConfig.is_active}
                helperText="Points credited to the new user on their first delivered order"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="number" label="Minimum expiry (days)"
                value={earningConfig.min_expiry_days}
                onChange={handleEarningChange('min_expiry_days')}
                disabled={!earningConfig.is_active}
                helperText="Shortest window before credited points expire"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="number" label="Maximum expiry (days)"
                value={earningConfig.max_expiry_days}
                onChange={handleEarningChange('max_expiry_days')}
                disabled={!earningConfig.is_active}
                helperText="Longest window before credited points expire"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="number" label="Return window (days)"
                value={earningConfig.return_window_days}
                onChange={handleEarningChange('return_window_days')}
                disabled={!earningConfig.is_active}
                helperText="Points stay locked (earned but not spendable) until this many days after crediting, to cover returns"
              />
            </Grid>
          </Grid>

          <Button
            variant="contained" onClick={handleEarningSave} disabled={earningSaving}
            sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005c64' }, textTransform: 'none', fontWeight: 600, mt: 2 }}
          >
            {earningSaving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Save earning settings'}
          </Button>
        </Paper>
      )}

      {/* ══════════════════ GLOBAL POINT VALUE ══════════════════ */}
      {!pointValueConfig ? (
        <Alert
          severity="error"
          sx={{ mb: 3, maxWidth: 900 }}
          action={<Button color="inherit" size="small" onClick={fetchPointValueConfig} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          Failed to load point value settings.
        </Alert>
      ) : (
        <Paper sx={{ p: 3, maxWidth: 900, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PaidIcon sx={{ color: '#006D77' }} />
            <Typography variant="h6" fontWeight="bold">Wallet point value</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The rupee value of one wallet point. This is a single global rate shared across
            every service — medicine, diagnostic, nursing, and physiotherapy all redeem
            points at this same rate. Never shown to users directly.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth type="number" label="Rupees per point"
                value={pointValueConfig.point_value}
                onChange={handlePointValueChange}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                helperText="e.g. 1.00 = ₹1 per point"
              />
            </Grid>
          </Grid>

          <Button
            variant="contained" onClick={handlePointValueSave} disabled={pointValueSaving}
            sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005c64' }, textTransform: 'none', fontWeight: 600, mt: 2 }}
          >
            {pointValueSaving ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Save point value'}
          </Button>
        </Paper>
      )}

      {/* ══════════════════ REDEMPTION SECTION (% of bill) ══════════════════ */}
      {!serviceConfigs ? (
        <Alert
          severity="error"
          sx={{ maxWidth: 900 }}
          action={<Button color="inherit" size="small" onClick={fetchServiceConfigs} sx={{ textTransform: 'none' }}>Retry</Button>}
        >
          Failed to load reward redemption settings.
        </Alert>
      ) : (
        <Paper sx={{ p: 3, maxWidth: 900 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <SettingsIcon sx={{ color: '#006D77' }} />
            <Typography variant="h6" fontWeight="bold">Reward point redemption settings</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure whether points can be redeemed on each service, and the maximum
            percentage of the bill that can be covered using points. For example, setting
            Medicine to 10% means a customer can pay at most 10% of a medicine order's
            total using their wallet points — the rest must be paid normally.
          </Typography>

          {SERVICE_ORDER.map((serviceType, idx) => {
            const cfg = serviceConfigs[serviceType] || DEFAULT_SERVICE_CONFIGS[serviceType];
            // isSaving is true for EVERY service button while ANY save is in
            // flight, so clicking "Save Medicine" also spins/disables the
            // Diagnostic, Nursing, and Physiotherapy buttons until it resolves.
            const isSaving = savingKey !== null;

            return (
              <Box key={serviceType} sx={{ mb: idx < SERVICE_ORDER.length - 1 ? 3 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    {cfg.display_name || serviceType}
                  </Typography>
                  <Chip
                    size="small"
                    label={cfg.redemption_enabled ? 'Redemption ON' : 'Redemption OFF'}
                    sx={{
                      bgcolor: cfg.redemption_enabled ? '#DCFCE7' : '#FEE2E2',
                      color: cfg.redemption_enabled ? '#166534' : '#B91C1C',
                      fontWeight: 700,
                    }}
                  />
                </Box>

                <FormControlLabel
                  sx={{ mb: 1.5 }}
                  control={
                    <Switch
                      checked={!!cfg.redemption_enabled}
                      onChange={handleServiceChange(serviceType, 'redemption_enabled')}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#006D77' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#006D77' },
                      }}
                    />
                  }
                  label="Allow points to be redeemed on this service"
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max % of bill redeemable via points"
                      value={cfg.max_redemption_percentage ?? ''}
                      onChange={handleServiceChange(serviceType, 'max_redemption_percentage')}
                      disabled={!cfg.redemption_enabled}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      helperText="e.g. 10 = points can cover up to 10% of the bill for this service"
                      inputProps={{ min: 0, max: 100, step: 1 }}
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleServiceSave(serviceType)}
                  disabled={isSaving}
                  sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005c64' }, textTransform: 'none', fontWeight: 600, mt: 2 }}
                >
                  {isSaving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Save ${cfg.display_name || serviceType} settings`}
                </Button>

                {idx < SERVICE_ORDER.length - 1 && <Divider sx={{ mt: 3 }} />}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* ══════════════════ SUCCESS DIALOG (shown after ANY save) ══════════════════ */}
      <Dialog open={successDialog.open} onClose={closeSuccessDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: '#2e7d32' }} />
          Saved successfully
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {successDialog.title}. The backend confirmed the following values were saved:
          </Typography>
          {successDialog.data && (
            <Stack spacing={1}>
              {Object.entries(successDialog.data)
                // skip noisy/internal fields that aren't useful to show
                .filter(([key]) => !['id', '_id', '__v'].includes(key))
                .map(([key, value]) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid #eee',
                      py: 0.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {humanizeKey(key)}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value ?? '—')}
                    </Typography>
                  </Box>
                ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeSuccessDialog}
            variant="contained"
            sx={{ bgcolor: '#006D77', '&:hover': { bgcolor: '#005c64' }, textTransform: 'none', fontWeight: 600 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RewardConfigTab;